import { PersistPartial } from 'redux-persist/es/persistReducer';
import { CHECKOUT_PARAMS_CONFIG, PAYMENT_METHOD_SOURCE_CONFIG, TCartKey } from 'core/constants/checkout.constants';
import { DEFAULT_CURRENCY } from 'core/constants/currency.constants';
import { EPaymentMethodType } from 'core/model/enums/checkout.enum';
import { ECustomerSource } from 'core/model/enums/customer.enum';
import { IBusiness } from 'core/model/interfaces/business.interface';
import {
  ICheckoutDetail,
  ICheckoutDetailGroup,
  ICheckoutDetailParams,
  ICheckoutPaymentMethod,
  TShippingDeliveryDates,
} from 'core/model/interfaces/checkout.interface';
import { ICreditLimit } from 'core/model/interfaces/credits.interface';
import { IUser } from 'core/model/interfaces/customer.interface';
import { isFill } from 'shared/utils/string.utils';
import {
  ICanBuyForCustomerSource,
  ICheckoutState,
  IInitialCheckoutParams,
  IInitialRefetchCheckoutParams,
} from './types';

export const extractCurrency = (detail?: ICheckoutDetailGroup) => {
  if (detail) {
    return detail.groupingRule?.products?.[0]?.currency || DEFAULT_CURRENCY;
  }
  return DEFAULT_CURRENCY;
};

export const hasProductsInGroupingRules = (detail: ICheckoutDetailGroup) => detail.groupingRule?.products?.length > 0;

export const canBuyByDistributor = ({ method, hasProducts, isPurchaseBlocked }: ICanBuyForCustomerSource) => {
  if (!hasProducts) return true;

  if (isPurchaseBlocked) return false;

  if (!isFill(method.id)) return false;

  return true;
};

export const hasCreditInsufficiency = (creditLimit: ICreditLimit, detail: ICheckoutDetailGroup) =>
  creditLimit?.availableAmount < detail.amounts?.final;

export const mapToInitialRefetchCheckoutParams = ({
  customerSource,
  paymentMethod,
  distributor,
}: IInitialRefetchCheckoutParams) => {
  const payload: ICheckoutDetailParams = {};
  const config = CHECKOUT_PARAMS_CONFIG[customerSource];

  if (customerSource === ECustomerSource.HYBRID) {
    if (config.alicorp) payload[config.alicorp] = paymentMethod.alicorpCart.id;
    if (config.allied) payload[config.allied] = paymentMethod.alliedCart.id;
  } else {
    const paymentMethodId = paymentMethod[distributor].id;
    if (config.alicorp) payload[config.alicorp] = paymentMethodId;
    if (config.allied) payload[config.allied] = paymentMethodId;
  }

  return payload;
};

export const getAddressIdBySource = (address: IBusiness | Partial<IBusiness>) => {
  const alicorpAddressId = address?.ids?.alicorp || undefined;
  const alliedAddressId = address?.ids?.allied || undefined;

  return {
    alicorpAddressId,
    alliedAddressId,
  };
};

export const getInitialCheckoutParams = ({
  checkoutDetail,
  checkoutPaymentMethod,
  checkoutAddress,
  customerSource,
}: IInitialCheckoutParams) => {
  const paymentMethods = getPaymentMethodsBySource(checkoutDetail, customerSource);
  const paymentMethodSelected = getPaymentMethodsSelected(checkoutPaymentMethod, customerSource);

  const foundPaymentMethodDex = paymentMethods.dex?.find(pm => pm.id === paymentMethodSelected.dex?.id);
  const foundPaymentMethodApudex = paymentMethods.apudex?.find(pm => pm.id === paymentMethodSelected.apudex?.id);

  const payload: ICheckoutDetailParams = {};
  const config = CHECKOUT_PARAMS_CONFIG[customerSource];

  if (config.alicorp && foundPaymentMethodDex?.id) {
    payload[config.alicorp] = foundPaymentMethodDex.id;
  }

  if (config.allied && foundPaymentMethodApudex?.id) {
    payload[config.allied] = foundPaymentMethodApudex.id;
  }

  const { alicorpAddressId, alliedAddressId } = getAddressIdBySource(checkoutAddress);

  payload.alicorpAddressId = alicorpAddressId;
  payload.alliedAddressId = alliedAddressId;

  return payload;
};

export const getInitialCheckoutValues = (detail: ICheckoutDetail, customer: IUser) => {
  const { source } = customer;

  const paymentMethodsBySource = getPaymentMethodsBySource(detail, source);

  const groupingRules = [
    detail.generalCart?.groupingRule,
    detail.alicorpCart?.groupingRule,
    detail.alliedCart?.groupingRule,
  ].filter(Boolean);

  const deliveryDates = groupingRules.reduce<Required<TShippingDeliveryDates>>(
    (acc, current) => {
      if (current) {
        const firstDate = current.dates?.[0];
        if (firstDate) {
          acc[current.distributor][0] = firstDate;
        }
      }
      return acc;
    },
    {
      alicorpCart: {},
      alliedCart: {},
      generalCart: {},
    },
  );

  const foundPaymentMethodDex = paymentMethodsBySource.dex?.find(pm => pm.type === EPaymentMethodType.IN_CASH);
  const foundPaymentMethodApudex = paymentMethodsBySource.apudex?.find(pm => pm.type === EPaymentMethodType.IN_CASH);

  const selectedPaymentMethodDex = {
    code: foundPaymentMethodDex?.code,
    id: foundPaymentMethodDex?.id,
  };

  const selectedPaymentMethodApudex = {
    code: foundPaymentMethodApudex?.code,
    id: foundPaymentMethodApudex?.id,
  };

  const paymentMethod: ICheckoutPaymentMethod = {
    alicorpCart: {},
    alliedCart: {},
    generalCart: {},
  };

  if (source === ECustomerSource.HYBRID) {
    paymentMethod.alicorpCart = selectedPaymentMethodDex;
    paymentMethod.alliedCart = selectedPaymentMethodApudex;
  } else {
    paymentMethod.generalCart = selectedPaymentMethodApudex.id ? selectedPaymentMethodApudex : selectedPaymentMethodDex;
  }

  return {
    deliveryDates,
    paymentMethod,
  };
};

export const getCustomerIdFromCheckout = (checkout: ICheckoutState & PersistPartial): number => {
  const {
    address: { customerId },
  } = checkout;

  return customerId ?? 0;
};

export const getPaymentMethodsBySource = (detail: ICheckoutDetail, sourceId: ECustomerSource) => {
  const config = PAYMENT_METHOD_SOURCE_CONFIG[sourceId];

  const getPaymentMethods = (cartKey: TCartKey | null) =>
    cartKey ? detail[cartKey]?.groupingRule?.paymentMethods : undefined;

  return {
    dex: getPaymentMethods(config.dex),
    apudex: getPaymentMethods(config.apudex),
  };
};

export const getPaymentMethodsSelected = (paymentMethod: ICheckoutPaymentMethod, customerSource: ECustomerSource) => {
  const config = PAYMENT_METHOD_SOURCE_CONFIG[customerSource];

  const getSelectedMethod = (cartKey: TCartKey | null) => (cartKey ? paymentMethod[cartKey] : undefined);

  return {
    dex: getSelectedMethod(config.dex),
    apudex: getSelectedMethod(config.apudex),
  };
};
